import { createFileRoute } from '@tanstack/react-router'
import { Button, Form, Input, Modal, Space, Table } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useState } from 'react'
import {
  useBlacklistList,
  useCreateBlacklist,
  useDeleteBlacklist,
} from '../hooks/useBlacklist'
import { getPagination } from '../components/TablePagination'
import type { FilterDropdownProps } from 'antd/es/table/interface'
import type { Blacklist } from '../services/blacklistService'

export const Route = createFileRoute('/blacklist')({
  component: BlacklistPage,
})

function BlacklistPage() {
  const [searchParams, setSearchParams] = useState({
    pageNum: '1',
    pageSize: '10',
    name: '',
    phone: '',
  })
  const [form] = Form.useForm()
  const [isModalOpen, setIsModalOpen] = useState(false)

  // 使用 hooks 获取数据和操作方法
  const {
    data: blacklistData,
    isLoading,
    refetch,
  } = useBlacklistList(searchParams)
  const createMutation = useCreateBlacklist()
  const deleteMutation = useDeleteBlacklist()

  const handleNameFilter = (value: string) => {
    setSearchParams((prev) => ({
      ...prev,
      name: value,
      pageNum: '1', // 重置到第一页
    }))
  }

  const handlePhoneFilter = (value: string) => {
    setSearchParams((prev) => ({
      ...prev,
      phone: value,
      pageNum: '1', // 重置到第一页
    }))
  }

  const handleAdd = () => {
    form.validateFields().then((values) => {
      createMutation.mutate(values, {
        onSuccess: () => {
          form.resetFields()
          setIsModalOpen(false)
          refetch()
        },
      })
    })
  }

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这条黑名单记录吗？',
      onOk: () => {
        deleteMutation.mutate(id, {
          onSuccess: () => {
            refetch()
          },
        })
      },
    })
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="搜索姓名"
            value={props.selectedKeys[0]}
            onChange={(e) =>
              props.setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              props.confirm?.()
              handleNameFilter((props.selectedKeys[0] as string) || '')
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                props.confirm?.()
                handleNameFilter((props.selectedKeys[0] as string) || '')
              }}
              size="small"
              style={{ width: 90 }}
            >
              搜索
            </Button>
            <Button
              onClick={() => {
                props.clearFilters?.()
                handleNameFilter('')
              }}
              size="small"
              style={{ width: 90 }}
            >
              重置
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      filteredValue: searchParams.name ? [searchParams.name] : undefined,
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="搜索手机号"
            value={props.selectedKeys[0]}
            onChange={(e) =>
              props.setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              props.confirm?.()
              handlePhoneFilter((props.selectedKeys[0] as string) || '')
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                props.confirm?.()
                handlePhoneFilter((props.selectedKeys[0] as string) || '')
              }}
              size="small"
              style={{ width: 90 }}
            >
              搜索
            </Button>
            <Button
              onClick={() => {
                props.clearFilters?.()
                handlePhoneFilter('')
              }}
              size="small"
              style={{ width: 90 }}
            >
              重置
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      filteredValue: searchParams.phone ? [searchParams.phone] : undefined,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: Blacklist) => (
        <Space size="small">
          <Button size="small" danger onClick={() => handleDelete(record.id)}>
            删除
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">黑名单管理</h2>
        <Button type="primary" onClick={() => setIsModalOpen(true)}>
          添加黑名单
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={blacklistData?.records || []}
        rowKey="id"
        bordered
        loading={isLoading}
        pagination={getPagination(
          parseInt(searchParams.pageNum),
          parseInt(searchParams.pageSize),
          blacklistData?.total || 0,
          (page, pageSize) => {
            setSearchParams((prev) => ({
              ...prev,
              pageNum: page.toString(),
              pageSize: pageSize.toString(),
            }))
          },
        )}
      />

      <Modal
        title="添加黑名单"
        open={isModalOpen}
        onOk={handleAdd}
        onCancel={() => setIsModalOpen(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input placeholder="请输入姓名" />
          </Form.Item>
          <Form.Item
            name="phone"
            label="手机号"
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' },
            ]}
          >
            <Input placeholder="请输入手机号" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
